/**
 * Tenants API Client
 *
 * Super admin only - manages tenants/organizations
 */

import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  stats?: {
    user_count: number;
    template_count: number;
    instance_count: number;
  };
}

export interface TenantListResponse {
  items: Tenant[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenantCreate {
  name: string;
  slug: string;
  settings?: Record<string, unknown>;
}

export interface TenantUpdate {
  name?: string;
  slug?: string;
  settings?: Record<string, unknown>;
  is_active?: boolean;
}

export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
  joined_at: string;
  role: {
    id: string;
    name: string;
    permissions: string[];
  };
}

export interface ListTenantsParams {
  search?: string;
  is_active?: boolean;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// ============================================================================
// API Client
// ============================================================================

export const tenantsApi = {
  /**
   * List all tenants (super admin only)
   */
  list: async (params?: ListTenantsParams): Promise<TenantListResponse> => {
    const queryParams: Record<string, string> = {};
    if (params?.search) queryParams.search = params.search;
    if (params?.is_active !== undefined) queryParams.is_active = String(params.is_active);
    if (params?.sort_by) queryParams.sort_by = params.sort_by;
    if (params?.sort_order) queryParams.sort_order = params.sort_order;
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.offset) queryParams.offset = String(params.offset);

    const response = await apiClient.get('/v1/tenants', { params: queryParams });
    return response.data;
  },

  /**
   * Get a single tenant with stats
   */
  get: async (id: string): Promise<Tenant> => {
    const response = await apiClient.get(`/v1/tenants/${id}`);
    return response.data;
  },

  /**
   * Create a new tenant
   */
  create: async (data: TenantCreate): Promise<Tenant> => {
    const response = await apiClient.post('/v1/tenants', data);
    return response.data;
  },

  /**
   * Update a tenant
   */
  update: async (id: string, data: TenantUpdate): Promise<Tenant> => {
    const response = await apiClient.put(`/v1/tenants/${id}`, data);
    return response.data;
  },

  /**
   * Deactivate a tenant (soft delete)
   */
  deactivate: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/tenants/${id}`);
  },

  /**
   * Get users in a tenant
   */
  getUsers: async (tenantId: string): Promise<{ items: TenantUser[]; total: number }> => {
    const response = await apiClient.get(`/v1/tenants/${tenantId}/users`);
    return response.data;
  },
};
